import { useState, useEffect } from 'react';
import { Languages } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface FeaturesTranslation {
  [locale: string]: string[];
}

interface FeaturesTranslationDialogProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  features: string[];
  translations?: FeaturesTranslation;
  onChange: (_translations: FeaturesTranslation) => void;
  supportedLanguages?: string[];
}

const ALL_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
];

export function FeaturesTranslationDialog({ 
  open, 
  onOpenChange, 
  features,
  translations = {}, 
  onChange,
  supportedLanguages = ['en']
}: FeaturesTranslationDialogProps) {
  const { t } = useTranslation();
  const [localTranslations, setLocalTranslations] = useState<FeaturesTranslation>({});
  
  // Filter languages based on supportedLanguages prop
  const SUPPORTED_LANGUAGES = ALL_LANGUAGES.filter(lang => supportedLanguages.includes(lang.code));

  useEffect(() => {
    // Initialize when dialog opens or when features change
    if (open) {
      const supportedLangs = ALL_LANGUAGES.filter(lang => supportedLanguages.includes(lang.code));
      const initial: FeaturesTranslation = { en: features };
      supportedLangs.forEach(lang => {
        if (lang.code === 'en') {
          initial[lang.code] = features;
        } else {
          // Keep existing translations but ensure we have the right number of items
          const existingTranslations = translations[lang.code] || [];
          initial[lang.code] = features.map((_, index) => 
            existingTranslations[index] || ''
          );
        }
      });
      setLocalTranslations(initial);
    }
  }, [open, features, supportedLanguages, translations]);

  const handleFeatureChange = (locale: string, index: number, value: string) => {
    setLocalTranslations(prev => ({
      ...prev,
      [locale]: prev[locale].map((f, i) => i === index ? value : f)
    }));
  };

  const handleSave = () => {
    onChange(localTranslations);
    onOpenChange(false);
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Languages className="h-5 w-5" />
            {t('products.translateFeatures')}
          </DialogTitle>
          <DialogDescription>
            {t('products.translateFeaturesDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {SUPPORTED_LANGUAGES.length === 1 ? (
            <Alert>
              <AlertDescription>
                {t('products.noAdditionalLanguages')}
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {/* Translation progress removed as requested */}

              <Tabs defaultValue={SUPPORTED_LANGUAGES[0]?.code} className="h-full">
                <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${SUPPORTED_LANGUAGES.length}, 1fr)` }}>
                  {SUPPORTED_LANGUAGES.map(lang => (
                    <TabsTrigger key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                <div className="mt-4 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 300px)' }}>
                  {SUPPORTED_LANGUAGES.map(lang => (
                    <TabsContent key={lang.code} value={lang.code} className="space-y-4">
                      <div className="space-y-3">
                        {features.map((feature, index) => (
                          <div key={index} className="space-y-1">
                            <Label className="text-sm text-muted-foreground">
                              {t('products.feature')} {index + 1}
                              {lang.code !== 'en' && (
                                <span className="ml-2 text-xs">
                                  ({t('products.original')}: {feature})
                                </span>
                              )}
                            </Label>
                            <Input
                              value={localTranslations[lang.code]?.[index] || ''}
                              onChange={(e) => handleFeatureChange(lang.code, index, e.target.value)}
                              placeholder={lang.code === 'en' ? t('products.featurePlaceholder') : feature}
                              disabled={lang.code === 'en'}
                            />
                          </div>
                        ))}
                      </div>
                    </TabsContent>
                  ))}
                </div>
              </Tabs>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
